import React, { useCallback, useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import supabase from "../../supabaseClient";
import logo from "../../assets/RCS-Logo-Blue.png";

const initialPodForm = {
  consignee: "",
  deliveryAddress: "",
  deliveryAddress2: "",
  shippers: "",
  awbBl: "",
  packages: [{ noOfPkgs: "", weight: "", description: "" }],
  deliveredBy: "",
  truckNo: "",
  deliveryDate: "",
  receivedBy: "",
  receivedDate: "",
  receivedTime: "",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const toText = (value) => (value === null || value === undefined ? "" : String(value));

const resolvePackages = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatAddress = (shipment) =>
  [shipment?.receiver_building, shipment?.receiver_street, shipment?.receiver_city, shipment?.receiver_country]
    .filter(Boolean)
    .join(", ");

const DocumentTemplates = () => {
  const user = useSelector((state) => state.auth.user);
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPodNumber, setCurrentPodNumber] = useState(1000);
  const [activeTab, setActiveTab] = useState("create");
  const [podDocuments, setPodDocuments] = useState([]);
  const [podListLoading, setPodListLoading] = useState(false);
  const [podSearchTerm, setPodSearchTerm] = useState("");
  const [podForm, setPodForm] = useState(initialPodForm);

  const filteredShipments = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return shipments.filter((shipment) => {
      if (!search) return true;
      return (
        shipment.tracking_no?.toLowerCase().includes(search) ||
        shipment.receiver_name?.toLowerCase().includes(search) ||
        shipment.shipper_name?.toLowerCase().includes(search)
      );
    });
  }, [searchTerm, shipments]);

  const filteredPodDocuments = useMemo(() => {
    const search = podSearchTerm.toLowerCase();
    return podDocuments.filter((pod) => {
      if (!search) return true;
      return (
        pod.pod_number?.toLowerCase().includes(search) ||
        pod.consignee?.toLowerCase().includes(search) ||
        pod.shippers?.toLowerCase().includes(search) ||
        pod.awb_bl?.toLowerCase().includes(search) ||
        pod.tracking_no?.toLowerCase().includes(search)
      );
    });
  }, [podDocuments, podSearchTerm]);

  const loadShipments = useCallback(async () => {
    const { data, error } = await supabase.from("shipments").select("*").order("date", { ascending: false });
    if (error) {
      console.error("Error fetching shipments:", error.message);
      return;
    }
    setShipments(data || []);
  }, []);

  const loadPodNumber = useCallback(async () => {
    const { data, error } = await supabase
      .from("document_series")
      .select("current_number")
      .eq("document_type", "POD")
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        await supabase.from("document_series").insert({ document_type: "POD", current_number: 1000 });
        setCurrentPodNumber(1000);
      } else {
        console.error("Error fetching POD number:", error.message);
      }
      return;
    }

    if (data) {
      setCurrentPodNumber(Number(data.current_number || 1000));
    }
  }, []);

  const loadPodDocuments = useCallback(async () => {
    setPodListLoading(true);
    const { data, error } = await supabase.from("pod_documents").select("*").order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching POD documents:", error.message);
    } else {
      setPodDocuments(data || []);
    }
    setPodListLoading(false);
  }, []);

  useEffect(() => {
    loadShipments();
    loadPodNumber();
  }, [loadShipments, loadPodNumber]);

  useEffect(() => {
    loadPodDocuments();
  }, [loadPodDocuments, currentPodNumber]);

  const handleSelectShipment = (shipment) => {
    setSelectedShipment(shipment);
    setIsManualEntry(false);
    setPodForm((prev) => ({
      ...prev,
      consignee: shipment.receiver_name || "",
      deliveryAddress: formatAddress(shipment) || "",
      deliveryAddress2:
        [shipment.receiver_city || "", shipment.receiver_country || ""].filter(Boolean).join(", ") ||
        "",
      shippers: shipment.shipper_name || "",
      awbBl: shipment.tracking_no || "",
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setPodForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackageChange = (index, field, value) => {
    setPodForm((prev) => {
      const packages = [...prev.packages];
      packages[index] = { ...packages[index], [field]: value };
      return { ...prev, packages };
    });
  };

  const addPackageRow = () => {
    setPodForm((prev) => ({
      ...prev,
      packages: [...prev.packages, { noOfPkgs: "", weight: "", description: "" }],
    }));
  };

  const removePackageRow = (index) => {
    setPodForm((prev) => {
      if (prev.packages.length <= 1) return prev;
      return { ...prev, packages: prev.packages.filter((_, i) => i !== index) };
    });
  };

  const resetForm = () => {
    setPodForm(initialPodForm);
    setSelectedShipment(null);
    setIsManualEntry(false);
    setSearchTerm("");
  };

  const drawPodCopy = useCallback((pdf, podData, podNumber, yOffset) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const marginX = 18;
    const ly = (v) => yOffset + v;

    pdf.addImage(logo, "PNG", marginX, ly(6), 22, 11);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6);
    pdf.setTextColor(55, 75, 130);
    pdf.text("Retail C Ground Floor BM One Building Amvel Business Park Ninoy Aquino", pageWidth / 2, ly(8.5), {
      align: "center",
      maxWidth: pageWidth - marginX * 2,
    });
    pdf.text("Ave. San Dionisio 1700 City of Paranaque NCR, Fourth District Philippines", pageWidth / 2, ly(11), {
      align: "center",
      maxWidth: pageWidth - marginX * 2,
    });
    pdf.text("TRUNKLINE : (632) 8825 0200        FAX NO. : (632) 8829 5600", pageWidth / 2, ly(13.5), {
      align: "center",
      maxWidth: pageWidth - marginX * 2,
    });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(45, 90, 150);
    pdf.text("PROOF OF DELIVERY", pageWidth / 2, ly(19), { align: "center" });

    pdf.setFontSize(10);
    pdf.setTextColor(245, 130, 55);
    pdf.text(`No. ${podNumber}`, pageWidth - marginX, ly(19), { align: "right" });

    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);

    let y = 27;
    const drawField = (label, value) => {
      pdf.setFont("helvetica", "bold");
      pdf.text(label, marginX, ly(y));
      pdf.setFont("helvetica", "normal");
      pdf.text(toText(value), marginX + 30, ly(y), { maxWidth: pageWidth - marginX * 2 - 33 });
      pdf.line(marginX + 28, ly(y + 0.8), pageWidth - marginX, ly(y + 0.8));
      y += 6.5;
    };

    drawField("Consignee", podData.consignee);
    drawField("Delivery Address", podData.deliveryAddress || podData.delivery_address);
    drawField("", podData.deliveryAddress2 || podData.delivery_address_2);
    drawField("Shippers", podData.shippers);
    drawField("AWB / BL", podData.awbBl || podData.awb_bl);

    y += 2;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Received the following goods in good order and condition.", marginX, ly(y));
    y += 4;

    const packages = resolvePackages(podData.packages || podData.pod_packages);
    const bodyRows = (packages.length
      ? packages
      : [{ noOfPkgs: "", weight: "", description: "" }]
    ).map((pkg) => [
      toText(pkg.noOfPkgs || pkg.no_of_pkgs || ""),
      toText(pkg.weight || ""),
      toText(pkg.description || ""),
    ]);

    while (bodyRows.length < 4) {
      bodyRows.push(["", "", ""]);
    }

    pdf.autoTable({
      startY: ly(y),
      head: [["No. of Pkgs.", "Weight", "DESCRIPTION"]],
      body: bodyRows,
      theme: "grid",
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 7.5, cellPadding: 1.2, valign: "middle", lineColor: [80, 80, 80], lineWidth: 0.25 },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
      columnStyles: { 0: { cellWidth: 26, halign: "center" }, 1: { cellWidth: 24, halign: "center" }, 2: { cellWidth: "auto" } },
    });

    const tableEndY = pdf.lastAutoTable?.finalY || ly(y + 25);
    const signatureY = tableEndY + 8;
    pdf.setDrawColor(80);
    pdf.setLineWidth(0.25);
    pdf.line(marginX, signatureY, pageWidth - marginX, signatureY);

    const drawSignatureBlock = (x, label, subLabel) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.text(label, x, signatureY + 4, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      if (subLabel) {
        pdf.text(subLabel, x, signatureY + 7.5, { align: "center" });
      }
    };

    drawSignatureBlock(marginX + 32, "Delivered by:", "Our Checker / Driver");
    drawSignatureBlock(pageWidth - marginX - 32, "Received by:", "Signature over printed Name");

    const bottomY = signatureY + 13;
    pdf.setFontSize(6.5);
    pdf.text("Date: ____________", marginX, bottomY);
    pdf.text("Truck No.: ____________", marginX + 45, bottomY);
    pdf.text("Date: ____________", pageWidth - marginX - 48, bottomY);
    pdf.text("Time: ____________", pageWidth - marginX - 5, bottomY, { align: "right" });

    pdf.setFontSize(5.5);
    pdf.text(
      "Note: No breakage or shortage will be allowed for goods covered by this receipt unless exception is taken at the time of signature and same must be reported to this office at once.",
      marginX,
      bottomY + 5,
      { maxWidth: pageWidth - marginX * 2 }
    );

    return bottomY + 5;
  }, []);

  const generatePdfFromData = useCallback((podData, podNumber) => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 18;
    const sectionHeight = pageHeight / 2;

    drawPodCopy(pdf, podData, podNumber, 0);
    drawPodCopy(pdf, podData, podNumber, sectionHeight);

    pdf.setDrawColor(150);
    pdf.setLineDashPattern([2, 1.5], 0);
    pdf.setLineWidth(0.2);
    pdf.line(marginX, sectionHeight, pageWidth - marginX, sectionHeight);
    pdf.setLineDashPattern([], 0);

    return pdf;
  }, [drawPodCopy]);

  const getNextAvailablePodNumber = useCallback(async () => {
    const [{ data: seriesData }, { data: podRows }] = await Promise.all([
      supabase.from("document_series").select("current_number").eq("document_type", "POD").single(),
      supabase.from("pod_documents").select("pod_number"),
    ]);

    // pod_number is stored as varchar, so sorting/ordering it in SQL is
    // lexicographic (e.g. "0101" > "01004"). Compute the numeric max in JS instead.
    const maxExistingNumber = (podRows || []).reduce((max, row) => {
      const value = Number(row?.pod_number);
      return Number.isFinite(value) && value > max ? value : max;
    }, 0);

    const seriesNumber = Number(seriesData?.current_number || 1000);
    return Math.max(seriesNumber, maxExistingNumber + 1, 1000);
  }, []);

  const generatePodPdf = useCallback(async () => {
    setLoading(true);
    try {
      let nextNumber = await getNextAvailablePodNumber();
      const maxAttempts = 5;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const podNumber = String(nextNumber).padStart(5, "0");
        const podRecord = {
          pod_number: podNumber,
          document_type: "POD",
          consignee: podForm.consignee,
          delivery_address: podForm.deliveryAddress,
          delivery_address_2: podForm.deliveryAddress2,
          shippers: podForm.shippers,
          awb_bl: podForm.awbBl,
          packages: podForm.packages,
          delivered_by: podForm.deliveredBy,
          truck_no: podForm.truckNo,
          delivery_date: podForm.deliveryDate || null,
          received_by: podForm.receivedBy,
          received_date: podForm.receivedDate || null,
          received_time: podForm.receivedTime || null,
          shipment_id: selectedShipment?.shipment_id || null,
          tracking_no: selectedShipment?.tracking_no || podForm.awbBl,
          is_manual_entry: isManualEntry,
          created_by: user?.id || user?.user_id || null,
          created_by_name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : null,
        };

        const { error } = await supabase.from("pod_documents").insert(podRecord);

        if (error) {
          if (error.code === "23505") {
            nextNumber += 1;
            continue;
          }
          console.error("Error saving POD to database:", error);
          toast.error("Failed to save POD record to database");
          return;
        }

        await supabase.from("document_series").upsert({ document_type: "POD", current_number: nextNumber + 1 });
        setCurrentPodNumber(nextNumber + 1);

        const pdf = generatePdfFromData(podForm, podNumber);
        pdf.save(`POD-${podNumber}.pdf`);
        toast.success(`POD-${podNumber} generated successfully!`);
        resetForm();
        return;
      }

      toast.error("Failed to generate a unique POD number. Please try again.");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  }, [getNextAvailablePodNumber, generatePdfFromData, isManualEntry, podForm, selectedShipment, user]);

  const redownloadPod = useCallback(
    (pod) => {
      try {
        const pdf = generatePdfFromData(pod, pod.pod_number);
        pdf.save(`POD-${pod.pod_number}.pdf`);
        toast.success(`POD-${pod.pod_number} downloaded!`);
      } catch (error) {
        console.error("Error downloading PDF:", error);
        toast.error("Failed to download PDF");
      }
    },
    [generatePdfFromData]
  );

  return (
    <div className="p-6 font-poppins">
      <div className="mb-6 flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-blue">Document Templates</h1>
        <p className="text-grey">Proof of Delivery generation and history</p>
      </div>

      <div className="mb-6 flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("create")}
          className={`rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "create" ? "bg-blue text-white" : "bg-white text-blue border border-blue/20"}`}
        >
          Create POD
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("list")}
          className={`rounded-xl px-4 py-2 text-sm font-semibold ${activeTab === "list" ? "bg-blue text-white" : "bg-white text-blue border border-blue/20"}`}
        >
          POD List
        </button>
      </div>

      {activeTab === "create" ? (
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-blue">Proof of Delivery</h2>
                <p className="text-sm text-grey">Generate and save a POD document</p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-grey">
                <input
                  type="checkbox"
                  checked={isManualEntry}
                  onChange={(e) => setIsManualEntry(e.target.checked)}
                />
                Manual entry
              </label>
            </div>

            {!isManualEntry && (
              <div className="mb-5 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-grey">From Shipment</div>
                    <div className="text-sm text-grey">Search and select a shipment to auto-fill fields</div>
                  </div>
                  {selectedShipment && <span className="text-xs font-semibold text-blue">Selected</span>}
                </div>
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tracking no., consignee, shipper..."
                  className="mb-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                />
                <div className="max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white">
                  {filteredShipments.length ? (
                    filteredShipments.map((shipment) => (
                      <button
                        key={shipment.shipment_id}
                        type="button"
                        onClick={() => handleSelectShipment(shipment)}
                        className="flex w-full items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left hover:bg-blue/5"
                      >
                        <div>
                          <div className="font-semibold text-slate-900">{shipment.tracking_no || shipment.job_order || "—"}</div>
                          <div className="text-xs text-slate-500">{shipment.receiver_name || shipment.shipper_name || "-"}</div>
                        </div>
                        <div className="text-right text-xs text-slate-500">{shipment.shipment_description || "-"}</div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-grey">No shipments found.</div>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {[
                ["consignee", "Consignee"],
                ["shippers", "Shippers"],
                ["awbBl", "AWB / BL"],
                ["deliveredBy", "Delivered By"],
                ["truckNo", "Truck No."],
                ["deliveryDate", "Delivery Date", "date"],
                ["receivedBy", "Received By"],
                ["receivedDate", "Received Date", "date"],
                ["receivedTime", "Received Time", "time"],
              ].map(([name, label, type = "text"]) => (
                <div key={name}>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={podForm[name]}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                    placeholder={label}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Delivery Address</label>
                <input
                  name="deliveryAddress"
                  value={podForm.deliveryAddress}
                  onChange={handleInputChange}
                  className="mb-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Delivery address line 1"
                />
                <input
                  name="deliveryAddress2"
                  value={podForm.deliveryAddress2}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Delivery address line 2"
                />
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900">Packages</h3>
                <button
                  type="button"
                  onClick={addPackageRow}
                  className="rounded-xl border border-blue/20 bg-blue/5 px-3 py-2 text-xs font-semibold text-blue"
                >
                  + Add Package
                </button>
              </div>
              <div className="overflow-hidden rounded-2xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">No. of Pkgs.</th>
                      <th className="px-3 py-2 text-left">Weight</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {podForm.packages.map((row, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <input
                            value={row.noOfPkgs}
                            onChange={(e) => handlePackageChange(index, "noOfPkgs", e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={row.weight}
                            onChange={(e) => handlePackageChange(index, "weight", e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={row.description}
                            onChange={(e) => handlePackageChange(index, "description", e.target.value)}
                            className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removePackageRow(index)}
                            disabled={podForm.packages.length <= 1}
                            className="text-xs font-semibold text-red-600 disabled:opacity-40"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end">
              <button
                type="button"
                onClick={generatePodPdf}
                disabled={loading}
                className="rounded-xl bg-blue px-5 py-3 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Generating..." : "Generate POD"}
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="mb-2 text-lg font-semibold text-blue">Preview Notes</h3>
            <p className="text-sm text-grey">
              The POD is saved to <code>pod_documents</code> and the next number is tracked in <code>document_series</code>.
            </p>
            <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-slate-600">
              Current POD no: <span className="font-semibold text-slate-900">{String(currentPodNumber).padStart(5, "0")}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-blue">POD List</h2>
              <p className="text-sm text-grey">Re-download previously generated POD documents</p>
            </div>
            <input
              value={podSearchTerm}
              onChange={(e) => setPodSearchTerm(e.target.value)}
              placeholder="Search POD no., consignee, shipper..."
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm md:w-80"
            />
          </div>

          {podListLoading ? (
            <div className="py-10 text-center text-sm text-grey">Loading POD documents...</div>
          ) : filteredPodDocuments.length ? (
            <div className="overflow-hidden rounded-2xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">POD No.</th>
                    <th className="px-4 py-3 text-left">Consignee</th>
                    <th className="px-4 py-3 text-left">Shippers</th>
                    <th className="px-4 py-3 text-left">Mode</th>
                    <th className="px-4 py-3 text-left">Created</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPodDocuments.map((pod) => (
                    <tr key={pod.id}>
                      <td className="px-4 py-3 font-semibold">{pod.pod_number || "-"}</td>
                      <td className="px-4 py-3">{pod.consignee || "-"}</td>
                      <td className="px-4 py-3">{pod.shippers || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${pod.is_manual_entry ? "bg-gray-100 text-gray-700" : "bg-blue-50 text-blue-700"}`}>
                          {pod.is_manual_entry ? "Manual" : "From Shipment"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(pod.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => redownloadPod(pod)}
                          className="rounded-lg px-3 py-2 text-xs font-semibold text-blue hover:bg-blue/10"
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-grey">No POD documents found.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentTemplates;
